import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Music, Trees, Tent, Palette, Users, FerrisWheel } from 'lucide-react';
import SEO from '@/components/SEO';
import { InstagramStrip } from '@/components/InstagramStrip';

const HERO_IMAGE_DESKTOP = "/images/artwork/hero-cover.jpg";
const HERO_IMAGE_MOBILE = "/images/artwork/hero-story.jpg";

// Lineup data - exact from user
const ariaStage = {
  name: 'Aria Stage',
  genre: 'Melodic Techno & Hard Dance',
  headliners: '8Kays • Agents of Time • Ammara • Bradeazy • Eelke Kleijn • Henri Bergmann • Innellea • Monkey Safari • Øostil • Pacs • Part Time Killer • Serafina • Silver Panda • Stephan Bodzin (live) • Zwilling',
  subheadliners: 'Kiara Friend • Liia • Lola Voss • Mad.Day • Shani • Staffy',
  support: 'Anton Yaz • Artisah • Caike • Daniel Tonik • Elle Cooper • Emma Bass • Jakob • Lady Blu • Lily Ro • Moritzio • Nadine B • Nick Reverse'
};

const mandalaStage = {
  name: 'Mandala Stage',
  genre: 'Psy-Tech & Progressive Psytrance',
  headliners: 'Astrix • Berg • Dang3r • Ghostrider • Interactive Noise • Liquid Soul • Morten Granau • Ranji • Ritmo • Shanti People • Sighter',
  subheadliners: 'Farseer • Grouch • Kyoshi • Ryanosaurus • Sammy La Marca • ShadowFX • Soul Alignment • Tetrameth • Triforce • Twisted Siblings',
  support: 'Ajem • Ben Zen • Blaize Bass • Boom Shankar • Carlos • Daas • Doof Nights • Eliad • Ishan • KnowHow • Party Explorers • Psychephilia'
};

const experiences = [
  { title: 'Music', description: 'World-class electronic music across two epic stages.', icon: Music, image: 'https://images.pexels.com/photos/1763075/pexels-photo-1763075.jpeg?auto=compress&cs=tinysrgb&w=800' },
  { title: 'Carnival Rides', description: 'Thrilling amusement rides and games for all ages.', icon: FerrisWheel, image: 'https://images.unsplash.com/photo-1502137840985-4ec07e8568bf?w=800' },
  { title: 'Nature', description: 'Escape to Maitland Showgrounds under the stars.', icon: Trees, image: 'https://images.pexels.com/photos/1687845/pexels-photo-1687845.jpeg?auto=compress&cs=tinysrgb&w=800' },
  { title: 'Camping', description: 'BYO camping or upgrade to premium glamping.', icon: Tent, image: 'https://images.pexels.com/photos/2422265/pexels-photo-2422265.jpeg?auto=compress&cs=tinysrgb&w=800' },
  { title: 'Art', description: 'Stunning installations and immersive experiences.', icon: Palette, image: 'https://images.pexels.com/photos/2747446/pexels-photo-2747446.jpeg?auto=compress&cs=tinysrgb&w=800' },
  { title: 'Community', description: 'Connect in workshops and healing spaces.', icon: Users, image: 'https://images.pexels.com/photos/2263436/pexels-photo-2263436.jpeg?auto=compress&cs=tinysrgb&w=800' },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroScale = useTransform(scrollYProgress, [0, 0.5], [1, 1.1]);

  return (
    <div className="bg-ff-blue" data-testid="home-page">
      <SEO 
        title="Electronic Music & Carnival Festival"
        description="Join us for 3 days of world-class electronic music and carnival experience at Maitland Showgrounds, NSW. September 25-27, 2026. Featuring Stephan Bodzin, Astrix, Innellea & 60+ artists."
      />
      {/* Hero Section */}
      <section ref={heroRef} className="relative h-screen" data-testid="hero-section">
        <motion.div style={{ scale: heroScale }} className="absolute inset-0">
          <img src={HERO_IMAGE_DESKTOP} alt="Frequency Forest" className="hidden sm:block w-full h-full object-cover" />
          <img src={HERO_IMAGE_MOBILE} alt="Frequency Forest" className="sm:hidden w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-ff-blue via-transparent to-transparent" />
        </motion.div>

        {/* Hero CTAs */}
        <div className="absolute bottom-12 sm:bottom-16 left-0 right-0 z-20 flex justify-center gap-4 px-6">
          <Link to="/lineup" className="btn-primary" data-testid="hero-lineup-btn">
            View Lineup
          </Link>
        </div>
      </section>

      {/* Lineup Section - Green background */}
      <section className="py-20 sm:py-32 bg-ff-green relative overflow-hidden" data-testid="lineup-preview-section">
        {/* Decorative floating illustrations */}
        <img src="/images/brand/butterfly.png" alt="" className="absolute top-8 right-8 w-16 sm:w-24 opacity-70 pointer-events-none animate-float" />
        <img src="/images/brand/flower1.png" alt="" className="absolute bottom-8 left-8 w-20 sm:w-32 opacity-60 pointer-events-none" />
        <img src="/images/brand/ladybug.png" alt="" className="absolute top-1/2 left-4 w-12 sm:w-16 opacity-50 pointer-events-none animate-float" style={{ animationDelay: '1s' }} />
        
        <div className="max-w-6xl mx-auto px-6 relative z-10">
          {/* Header with badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            {/* Date badge */}
            <div className="inline-flex items-center gap-2 mb-6">
              <span className="badge">25–27 SEP 2026</span>
              <span className="badge">Maitland Showgrounds</span>
            </div>
            
            <h2 className="font-display text-5xl sm:text-6xl md:text-7xl text-ff-brown mb-4">Lineup</h2>
            <p className="text-ff-brown/70 text-lg">Two stages. Three days. Listed A–Z within tier.</p>
          </motion.div>

          {/* Stages */}
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
            {/* Aria Stage - Pink/Green/Blue palette */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-ff-pink rounded-3xl p-6 sm:p-8 relative overflow-hidden"
            >
              <div className="relative z-10">
                <h3 className="font-supertaste text-3xl sm:text-4xl text-ff-brown mb-1">{ariaStage.name}</h3>
                <p className="text-ff-brown/60 text-sm font-semibold uppercase tracking-wide mb-6">{ariaStage.genre}</p>
                
                <div className="space-y-4">
                  <div>
                    <p className="text-ff-outline text-xs font-bold uppercase tracking-wider mb-2">Headliners</p>
                    <p className="text-ff-brown font-semibold leading-relaxed">{ariaStage.headliners}</p>
                  </div>
                  <div>
                    <p className="text-ff-outline text-xs font-bold uppercase tracking-wider mb-2">Sub-headliners</p>
                    <p className="text-ff-brown/80 leading-relaxed">{ariaStage.subheadliners}</p>
                  </div>
                  <div>
                    <p className="text-ff-outline text-xs font-bold uppercase tracking-wider mb-2">Support</p>
                    <p className="text-ff-brown/60 text-sm leading-relaxed">{ariaStage.support}</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Mandala Stage - Orange/Yellow/Blue palette */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-ff-orange rounded-3xl p-6 sm:p-8 relative overflow-hidden"
            >
              <div className="relative z-10">
                <h3 className="font-supertaste text-3xl sm:text-4xl text-ff-brown mb-1">{mandalaStage.name}</h3>
                <p className="text-ff-brown/60 text-sm font-semibold uppercase tracking-wide mb-6">{mandalaStage.genre}</p>
                
                <div className="space-y-4">
                  <div>
                    <p className="text-ff-outline text-xs font-bold uppercase tracking-wider mb-2">Headliners</p>
                    <p className="text-ff-brown font-semibold leading-relaxed">{mandalaStage.headliners}</p>
                  </div>
                  <div>
                    <p className="text-ff-outline text-xs font-bold uppercase tracking-wider mb-2">Sub-headliners</p>
                    <p className="text-ff-brown/80 leading-relaxed">{mandalaStage.subheadliners}</p>
                  </div>
                  <div>
                    <p className="text-ff-outline text-xs font-bold uppercase tracking-wider mb-2">Support</p>
                    <p className="text-ff-brown/60 text-sm leading-relaxed">{mandalaStage.support}</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mt-10"
          >
            <Link
              to="/lineup"
              className="btn-primary inline-block"
              data-testid="view-full-lineup-btn"
            >
              View Full Lineup
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Experience Section - Blue background */}
      <section className="py-20 sm:py-32 bg-ff-blue relative" data-testid="experience-section">
        <img src="/images/brand/kangaroo.png" alt="" className="absolute bottom-0 right-8 w-32 sm:w-48 opacity-40 pointer-events-none" />
        
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="badge mb-4">What Awaits You</span>
            <h2 className="font-display text-5xl sm:text-6xl text-ff-brown">The Experience</h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {experiences.map((exp, index) => (
              <motion.div
                key={exp.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`relative h-[280px] rounded-3xl overflow-hidden group ${index === 0 ? 'sm:col-span-2 lg:col-span-2 lg:row-span-2 lg:h-[580px]' : ''}`}
              >
                <img src={exp.image} alt={exp.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6 relative z-10">
                  <exp.icon className="w-8 h-8 text-white mb-3" />
                  <h3 className="font-supertaste text-2xl text-white mb-2">{exp.title}</h3>
                  <p className="text-white/80 text-sm">{exp.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <InstagramStrip />

      {/* CTA Section - Pink background */}
      <section className="py-20 sm:py-32 bg-ff-pink relative overflow-hidden" data-testid="cta-section">
        <img src="/images/brand/koala.png" alt="" className="absolute bottom-0 left-0 w-40 sm:w-64 opacity-50 pointer-events-none" />
        <img src="/images/brand/flower3.png" alt="" className="absolute top-8 right-8 w-24 sm:w-40 opacity-60 pointer-events-none animate-float" />
        <img src="/images/brand/flowers.png" alt="" className="absolute bottom-20 right-20 w-20 sm:w-32 opacity-40 pointer-events-none" />
        
        <div className="max-w-3xl mx-auto px-6 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-display text-5xl sm:text-6xl md:text-7xl text-ff-brown mb-6">
              Ready to enter the forest?
            </h2>
            <p className="text-ff-brown/70 text-lg mb-10 max-w-xl mx-auto">
              Join us for three days of world-class electronic music and carnival experience at Maitland Showgrounds.
            </p>
            <Link
              to="/lineup"
              className="btn-primary inline-flex items-center gap-2 text-lg"
              data-testid="cta-lineup-btn"
            >
              <Music className="w-5 h-5" />
              View Lineup
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
